import AutomationPoint from './AutomationPoint.js';
import {sleep} from 'utils/sleep.js';
import {quantizeStep, quantizeValue} from './quantizeStep.js';
import { createSelector } from 'reselect'
import {getValueAtStep, getValueExactlyAtStep} from './getValueAtStep.js';
import {setParameterAutomations} from 'actions/index.js';
import {setParameterMixAutomations} from 'actions/index.js';
import {uuid} from 'uuidv4';
import Tone from 'tone';
import {A_RATE, K_RATE} from 'constants/automation-rates.js';
import {isNumber} from 'utils/IsNumber.js';

const convertCache = {};

class Automations {
    constructor(parameter, isMix) {
        this.id = uuid();
        this.parameter = parameter;
        this.automations = [];
        this.nextCache = {};
        this.previousCache = {};
        this.getValueCache = {};
        this.distanceCache = {};
        this.isMix = isMix;
        this.eventIds = [];
        this.counter = 0;
        this.stepToValues = {};
    }

    get length() {
        return this.automations.length;
    }

    get sequencerEngine() {
        return this.parameter.sequencerEngine;
    }

    getPoint(point) {
        let ret = this.automations.find(p => p.id === point.id);
        return ret;
    }

    getSelectedPoints(fromStep, fromValue, toStep, toValue) {
        return this.automations.filter(
            ({step, value}) =>
                fromStep <= step && fromValue <= value &&
                toStep >= step && toValue >= value
        );
    }

    getClosestBefore(step) {
        // TODO: use bin. searc
        let previous = undefined;
        for (let i in this.automations) {
            let event = this.automations[i];
            if (event.step > step) {
                return previous;
            }

            previous = event;
        }
        return previous;
    }

    getDistanceToClosest(point) {
        let minDistance = undefined;

        if (point.step in this.distanceCache) {
            return this.distanceCache[point.step];
        }

        let minPoint = undefined;
        for (let i in this.automations) {
            let event = this.automations[i];
            let dist = Math.abs(event.step - point.step);
            if (minDistance === undefined ||
                dist < minDistance) {
                minDistance = dist;
                minPoint = event;
            }
        }
        this.distanceCache[point.step] = {
            distance: minDistance,
            point: minPoint
        };

        return this.distanceCache[point.step];
    }

    quantizePoint(point) {
        point = this.getPoint(point);
        if (point === undefined) {
            return;
        }
        point.step = quantizeStep(point.step);
        point.value = quantizeValue(this.parameter, point.value);
        for (let i in this.automations) {
            if (this.automations[i] === point) {
                this.automations[i] = point.getCopy();
            }
        }
        this.automations = [... this.automations];
        this.updateRedux();
    }

    movePoints(points, stepDiff, valueDiff) {
        for (let i in points) {
            let point = points[i];
            let {step, value} = point;
            step += stepDiff;
            value += valueDiff;
            this.movePoint(point, step, value, false);
        }
        
        this.schedule();
        this.updateRedux();
        this.clearCache();
    }

    movePoint(point, step, value, updateRedux=true) {
        if (point === undefined) {
            return;
        }
        point = this.getPoint(point);

        let nextEvent = this.getNextEvent(point);
        let previousEvent = this.getPreviousEvent(point);
        if ((nextEvent === undefined || step < nextEvent.step) &&
            (previousEvent === undefined || step > previousEvent.step)) {
            point.step = step; 
            point.value = Math.min(Math.max(value, this.parameter.min), this.parameter.max);
        } else {
            point.value = Math.min(Math.max(value, this.parameter.min), this.parameter.max);
        }

        for (let i in this.automations) {
            if (this.automations[i] === point) {
                this.automations[i] = point.getCopy();
                break;
            }
        }

        if (updateRedux) {
            this.schedule();
            this.updateRedux();
            this.clearCache();
        }
    }

    async updateRedux(force=false, shouldSleep=false) {
        if (shouldSleep) {
            let _counter = ++this.counter;
            await sleep(100);
            if (_counter !== this.counter) {
                return;
            } 
        }
        if (this.store && (force || this.automations.length > 0)) {
            // let redux know about this automation
            // this allows UI to show correct automation AND
            // PlayableSequencerEngine to schedule it
            if (this.isMix) {
                this.store.dispatch(setParameterMixAutomations(
                    this.parameter, [... this.automations], this));
            } else {
                this.store.dispatch(setParameterAutomations(
                    this.parameter, [... this.automations], this));
            }
        }
    }

    hasEventsAfterStep(step) {
        // todo: might need to use a cache for this shit
        let lastEvent = this.automations[this.automations.length - 1];
        return lastEvent && lastEvent.step > step;
    }

    getNextEvent(point, automations=this.automations) {
        if (point.id in this.nextCache) {
            return this.nextCache[point.id];
        } 
        
        for (let i=0; i < automations.length; i++) {
            if (point.id === automations[i].id) {
                this.nextCache[point.id] = automations[i + 1];
                return automations[i + 1];
            }
        }

        this.nextCache[point.id] = automations[0];
        return automations[0];
    }

    getPreviousEvent(point) {
        let previousEvent = undefined;
        if (point.id in this.previousCache) {
            return this.previousCache[point.id];
        }

        for (let i=0; i < this.automations.length - 1; i++) {
            if (this.automations[i] === point) {
                return previousEvent;
            }
            previousEvent = this.automations[i];
        }
        this.previousCache[point.id] = undefined;
        return undefined;
    }

    getValueAtStep(step) {
        if (this.parameter.track && !this.isMix) {
            let track = this.parameter.track.instrumentRack ?
                this.parameter.track.instrumentRack : this.parameter.track;
            step = step % track.length;
        } 
        if (step in this.getValueCache) {
            return this.getValueCache[step];
        }
        this.getValueCache[step] = getValueAtStep(step, this.parameter, this.automations);
        return this.getValueCache[step];
    }

    removeBreakPoints(points) {
        for (let point of points) {
            this.removeBreakPoint(point, false);
        }
        this.updateRedux(true);
    }

    removeAtStep(step) {
        let fullStep = step;
        if (this.parameter.track && !this.isMix) {
            let track = this.parameter.track.instrumentRack ?
                this.parameter.track.instrumentRack : this.parameter.track;
            step = step % track.length;
        }
        let time = new Date().getTime();
        let automations = [];
        let minDist = this.isMix ? 1200 : 300;
        for (let x of this.automations) {
            if (Math.floor(x.step) === step &&
                (x.addedAt === undefined  ||
                 time - x.addedAt > minDist)) {
                // remove this step since it has the step we are looking for
                // and hasn't been just added
            } else {
                automations.push(x);
            }
        }
        this.automations = automations;
    }

    removeBreakPoint(point, updateRedux=true) {
        if (point === undefined) {
            return;
        }
        point = this.getPoint(point);
        let i = this.automations.indexOf(point);
        if (i > -1) {
            this.automations.splice(i, 1);
        } 

        this.clearCache();
        if (updateRedux) {
            this.updateRedux(true);
        }
    }

    clearCache() {
        this.cache = {};
        this.getValueCache = {};
        this.nextCache = {};
        this.distanceCache = {};
        this.previousCache = {};
    }

    record(value, oldValue) {
        if (!this.sequencerEngine || !this.parameter.context || !this.parameter.context.track) {
            return undefined;
        }

        // need to figure out the exact step
        let currentStep = this.sequencerEngine.lastTrackStep;
        if (this.parameter.track && !this.isMix) {
            let track = this.parameter.track.instrumentRack ?
                this.parameter.track.instrumentRack : this.parameter.track;
            currentStep = currentStep % track.length;
        } 
        let currentStepTime = currentStep === this.lastStep ?
            this.lastTime : new Tone.Time(this.sequencerEngine.currentStepTime).toMilliseconds();
        this.lastStep = currentStep;
        this.lastTime = currentStepTime;
        let currentTime = new Tone.Time(Tone.Master.context.currentTime).toMilliseconds();
        let timeDiff = currentTime - currentStepTime;
        let time16n = new Tone.Time('32n').toMilliseconds();
        let stepsAfter = timeDiff / time16n;
        let stepNumber = currentStep + stepsAfter;

        let events = [];
        if (this.automations.length === 0 && this.isMix && this.parameter.config.exactAutomation) {
            events.push(this.addBreakPoint(0, oldValue, undefined, true, true));
        }
        if (this.isMix && !this.parameter.config.exactAutomation) {
            if (this.lastEvent &&
                Math.abs(stepNumber - this.lastEvent.step) > 4) {
                events.push(this.addBreakPoint(stepNumber - 1, oldValue, undefined, true, true));
            }
        }
        events.push(this.addBreakPoint(stepNumber, value, undefined, true, true));
        let key = Math.floor(stepNumber);
        if (!this.stepToValues[key]) {
            this.stepToValues[key]= [];
        }
        this.stepToValues[key].push(value);
        return events;
    }

    get lastEvent() {
        return this.automations[this.automations.length - 1];
    }

    addBreakPoint(step, value, id=uuid(), smoothen=false, shouldSleep=false) {
        if (step < 0) {
            step = 0;
        }
        this.clearCache();

        if (this.parameter.audioParam &&
            this.parameter.automationRate !== A_RATE) {
            this.parameter.automationRate = A_RATE;
        }
        let event = new AutomationPoint(step, value);
        event.id = id;
        if (this.sequencerEngine) {
            event.addedAt = new Date().getTime();
        }
        this.automations.push(event);
        this.automations.sort((a, b) => a.step - b.step);
        this.schedule();
        this.updateRedux(undefined, shouldSleep);
        return event;
    }

    get isRecording() {
        return this.store && this.store.getState().sequencer.isRecording;
    }

    get isSliderDown() {
        if (!this.store) {
            return false;
        }

        let parameterSliderDown = this.store.getState().automation.parameterSliderDown;
        return Array.isArray(parameterSliderDown) ?
            parameterSliderDown.indexOf(this.parameter) > -1 :
            parameterSliderDown === this.parameter;
    }

    clear() {
        // need a better way of scheduling here
        if (this.eventIds) {
            this.eventIds.forEach(id => Tone.Transport.clear(id));
            this.eventIds = [];
        }
    }

    // schedules a loop's worth of automations for this parameter
    schedule(startTime=this.lastStartTime, timeToSchedule=this.lastTimeToSchedule, time) {
        this.clear();
        if (startTime === undefined) {
            return;
        }

        this.lastStartTime = startTime;
        this.lastTimeToSchedule = timeToSchedule;

        if (this.parameter.audioParams === undefined &&
            this.parameter.audioParam === undefined) {
            // we dont need to schedule them, since theres nothing to schedule
            return;
        }

        // handle value at start of loop by adding an event at the start
        // with equal value to first step.
        // so when it starts it'll ensure it starts at that value
        let automations = this.automations;
        if (automations.length > 0) {
            let firstEvent = automations[0].getCopy();
            firstEvent.step = 0;
            firstEvent.id = uuid();
            automations = [firstEvent, ... automations];
        }

        let startValue = this.getValueAtStep(this.sequencerEngine.trackStep);
        // set the value of parameter now

        /*
        if (this.isMix) {
            this.setParameterValue(startValue, time);
        }
        */

        this.eventIds = automations.map(
            event => Tone.Transport.schedule(
                (time) => {
                    if (this.isSliderDown) {
                        return;
                    }
                    try {
                        if (this.parameter.audioParam || this.parameter.audioParams) {
                            let currentTime = Tone.Master.context.isOffline ?
                                Tone.Master.context._currentTime :
                                Tone.Master.context.currentTime;
                            let next = this.getNextEvent(event, automations);
                            if (!next) {
                                return;
                            }
                            let {value} = next;
                            if (value.toFrequency) {
                                value = value.toFrequency();
                            }
                            if (next) {
                                let diff = new Tone.Time('32n') * (next.step - event.step) - 0.01;
                                if (this.parameter.audioParams) {
                                    this.parameter.audioParams.forEach(
                                        p => p.rampTo(value, diff, time + 0.001));
                                } else if (this.parameter.audioParam) {
                                    if (this.parameter.audioParam._param &&
                                        this.parameter.audioParam.rampTo) {
                                        this.parameter.audioParam.rampTo(
                                            value,
                                            diff,
                                            time + 0.001);
                                    } else {
                                        // otherwise this is a web-audio AudioParam obj so
                                        // we need to use the web audio ramping func
                                        this.parameter.audioParam.linearRampToValueAtTime(
                                            value || 0.00001, currentTime + diff - 0.001);
                                    }
                                }
                            }
                        } else {
                        }
                    } catch (e) {
                        console.log("error while doing automation for parameter=", e, this.parameter);
                    }
                }, convertToTime(this.isMix ? event.step - startTime : event.step, startTime, timeToSchedule)));
    }

    start(time) {
        let startValue = this.getValueAtStep(this.sequencerEngine.trackStep);
        // set the value of parameter now

        if (this.isMix) {
            this.setParameterValue(startValue, time);
        }
    }

    setParameterValue(value, time) {
        if (this.parameter.audioParams) {
            this.parameter.audioParams.forEach(
                p => p.rampTo(value, 0.001, time));
        } else if (this.parameter.audioParam) {
            if (this.parameter.audioParam._param &&
                this.parameter.audioParam.rampTo) {
                this.parameter.audioParam.rampTo(
                    value, 0.001, time);
            } else {
                // otherwise this is a web-audio AudioParam obj so
                // we need to use the web audio ramping func
                this.parameter.audioParam.linearRampToValueAtTime(
                    value || 0.00001, Tone.Master.context.currentTime + 0.01);
            }
        }
    }
    
    getJson() {
        return {
            id: this.id,
            automations: this.automations.map(a => a.getJson())
        };
    }

    fromJson(json, schedule=true, isCopy=false) {
        let {id, automations} = json;

        if (!isCopy) {
            this.id = id;
        }

        if (automations === undefined || automations.length === 0) {
            return this;
        }
        for (let i in automations) {
            let {step, value, id} = automations[i];
            let event = new AutomationPoint()
                .fromJson(automations[i]);
            if (id !== undefined) {
                event.id = id;
            }
            let key = Math.floor(step);
            if (!this.stepToValues[key]) {
                this.stepToValues[key]= [];
            }
            this.stepToValues[key].push(value);
 
            this.automations.push(event);

        }

        if (schedule) {
            this.schedule();
        }

        this.updateRedux();
        return this;
    }

    getCopy(store=this.store) {
        let automations = new Automations(this.parameter);
        automations.fromJson(this.getJson(), false, true);
        automations.store = store;
        return automations;
    }

    dispose() {
        this.clear();

        this.automations = [];
        this.eventIds = [];
    }

    deleteAutomations() {
        this.clear();
        this.automations = [];
        this.stepToValues = {};
        this.updateRedux(true);
    }

    smoothen() {
        let dist = this.parameter.max - this.parameter.min;
        let minDist = dist * 0.03;
        let slopes = [];
        let previous = undefined;
        let automations = [];
        for (let event of this.automations) {
            if (previous) {
                let slope = (event.value  - previous.value) / (event.step - previous.step);
                if (slopes.length > 0) {
                    let dist = Math.abs(slope - peek(slopes));
                    let stepDist = Math.abs(event.step - previous.step);
                    if (stepDist > 16 || (dist > minDist && Math.floor(event.step) !== Math.floor(previous.step) )) {
                        // the change in slope is greater than the threshold
                        // thus we keep this automation pt
                        automations.push(event);
                    } else {
                        // the change in slope is less than the threshold
                        // thus we remove this automation pt
                        automations[automations.length - 1] = event;
                    }
                } else {
                    automations.push(event);
                }
                slopes.push(slope);
            } else {
                automations.push(event);
            }

            previous = event;
        }
        this.automations = automations;
        this.updateRedux();
    }
}

const peek = (l) => {
    return l[l.length - 1];
}

function convertToTime(step, startTime, time) {
    let ticks = Tone.Transport.getTicksAtTime(time);
    const TIME_32N = new Tone.Time('32n');
    let transportTime = new Tone.TransportTime(ticks + step * TIME_32N.toTicks(), "i");
    return transportTime;;
}

export default Automations;
